import { useEffect, useMemo, useRef, useState } from "react";
import { GENRES, GENRE_LIST, type Work } from "../data";
import type { Nav, Route } from "../types";
import { Placeholder } from "./atoms";
import { useT, useI18n } from "../i18n";
import { useAuth } from "../auth";
import { listWorks, fetchMyNotifications } from "../api";

interface HeaderProps {
  route: Route;
  nav: Nav;
  query: string;
  setQuery: (q: string) => void;
  isLoggedIn: boolean;
  onLogout: () => void;
}

type OpenMenu = "avatar" | "notif" | "genre" | null;

export function GlobalHeader({
  route,
  nav,
  query,
  setQuery,
  isLoggedIn,
  onLogout,
}: HeaderProps) {
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchVal, setSearchVal] = useState(query || "");
  const [suggestions, setSuggestions] = useState<Work[]>([]);
  const [notifications, setNotifications] = useState<
    Array<{ id: string; type: string; text: string; at: string; workId?: string }>
  >([]);
  const containerRef = useRef<HTMLElement>(null);
  const t = useT();
  const { user } = useAuth();

  useEffect(() => {
    setSearchVal(query || "");
  }, [query]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpenMenu(null);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // 検索サジェストを 250ms デバウンスで取得
  useEffect(() => {
    const q = searchVal.trim();
    if (!q) {
      setSuggestions([]);
      return;
    }
    let canceled = false;
    const t = setTimeout(() => {
      listWorks({ q, limit: 5 })
        .then((ws) => {
          if (!canceled) setSuggestions(ws);
        })
        .catch(() => {
          if (!canceled) setSuggestions([]);
        });
    }, 250);
    return () => {
      canceled = true;
      clearTimeout(t);
    };
  }, [searchVal]);

  // 通知メニューを開いたタイミングで取得
  useEffect(() => {
    if (openMenu === "notif" && isLoggedIn) {
      fetchMyNotifications()
        .then((ns) =>
          setNotifications(
            ns.map((n) => ({
              id: n.id,
              type: n.type,
              text: n.text,
              at: n.at,
              workId: n.workId,
            })),
          ),
        )
        .catch(() => setNotifications([]));
    }
  }, [openMenu, isLoggedIn]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setQuery(searchVal);
    nav({ page: "search", query: searchVal });
    setSearchFocused(false);
  }

  return (
    <header className="gheader" ref={containerRef}>
      <div className="container gheader-row">
        <a
          href="#"
          className="glogo"
          onClick={(e) => {
            e.preventDefault();
            nav({ page: "home" });
          }}
        >
          <span className="logo-p">Pix.</span>
          <span className="logo-brand">Studio Online</span>
          <span className="logo-sub desktop-only">/ Portal</span>
        </a>

        <nav className="gnav desktop-only">
          <span
            className={`navitem ${route.page === "search" ? "active" : ""}`}
            onClick={() => setOpenMenu(openMenu === "genre" ? null : "genre")}
          >
            {t("header.genre")}{" "}
            <span className="mono" style={{ fontSize: 9 }}>
              ▾
            </span>
          </span>
          <a
            href="#"
            className={route.page === "search" && route.query === undefined ? "active" : ""}
            onClick={(e) => {
              e.preventDefault();
              nav({ page: "search", sort: "popular" });
            }}
          >
            {t("header.ranking")}
          </a>
        </nav>

        {openMenu === "genre" && (
          <div className="gdrop" style={{ left: 170, top: 50, minWidth: 200 }}>
            <div className="gdrop-header">{t("header.genre")}</div>
            {GENRE_LIST.filter((g) => g.key !== "all").map((g) => (
              <a
                key={g.key}
                href="#"
                className="gdrop-item"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenMenu(null);
                  if (g.key === "all") return;
                  nav({ page: "search", genre: g.key });
                }}
              >
                <span className="ic">#</span>
                {g.ja}
              </a>
            ))}
          </div>
        )}

        <form className="gsearch desktop-only" onSubmit={submitSearch}>
          <span className="sicon">⌕</span>
          <input
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            placeholder={t("header.searchPlaceholder")}
            aria-label={t("header.searchAria")}
          />
          {searchFocused && (searchVal ? suggestions.length > 0 : true) && (
            <div className="gsearch-suggest">
              {searchVal ? (
                <>
                  <div className="sgroup-label">{t("header.workSuggestions")}</div>
                  {suggestions.map((w) => (
                    <div
                      key={w.id}
                      className="sitem"
                      onMouseDown={() => {
                        setSearchFocused(false);
                        nav({ page: "work", id: w.id });
                      }}
                    >
                      <Placeholder
                        caption=""
                        tone={GENRES[w.genre]?.tone ?? "other"}
                        seed={w.id.charCodeAt(3)}
                        className="sthumb"
                      />
                      <span>{w.title}</span>
                      <span className="smeta">{w.authorName ?? ""}</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="sgroup-label">{t("header.popularKeywords")}</div>
                  {["百合", "BL", "短編", "図書館", "ループもの"].map((k) => (
                    <div
                      key={k}
                      className="sitem"
                      onMouseDown={() => {
                        setSearchVal(k);
                        setQuery(k);
                        nav({ page: "search", query: k });
                        setSearchFocused(false);
                      }}
                    >
                      <span style={{ color: "var(--muted-2)" }}>⌕</span>
                      <span>{k}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </form>

        <div className="gright">
          <LangSwitch />
          <a
            href="#"
            className="gnav desktop-only"
            style={{ color: "var(--sub)", fontSize: 13, padding: "6px 10px" }}
            onClick={(e) => {
              e.preventDefault();
              nav({ page: "library" });
            }}
          >
            <span style={{ fontFamily: "var(--font-mono)", marginRight: 4 }}>▣</span>{" "}
            {t("header.library")}
          </a>

          {isLoggedIn && user ? (
            <>
              <button
                className="gbtn-notif desktop-only"
                aria-label={t("header.notifications")}
                onClick={() => setOpenMenu(openMenu === "notif" ? null : "notif")}
              >
                <span style={{ fontFamily: "var(--font-mono)" }}>♪</span>
                <span className="dot"></span>
              </button>
              <div style={{ position: "relative" }}>
                <button
                  className="gavatar"
                  onClick={() => setOpenMenu(openMenu === "avatar" ? null : "avatar")}
                >
                  <span className="avimg">
                    {user.displayName ? user.displayName.charAt(0) : "?"}
                  </span>
                  <span className="avname desktop-only">{user.displayName}</span>
                  <span className="avchev">▾</span>
                </button>
                {openMenu === "avatar" && (
                  <div className="gdrop" style={{ right: 0 }}>
                    <div className="gdrop-section">
                      <a
                        href="#"
                        className="gdrop-item"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenu(null);
                          nav({ page: "author", id: user.id });
                        }}
                      >
                        <span className="ic">@</span>
                        {t("header.profile")}
                      </a>
                      <a
                        href="#"
                        className="gdrop-item"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenu(null);
                          nav({ page: "library" });
                        }}
                      >
                        <span className="ic">▣</span>
                        {t("header.library")}
                      </a>
                    </div>
                    <div className="gdrop-section">
                      <div className="gdrop-header">{t("header.creator")}</div>
                      <a
                        href="#"
                        className="gdrop-item"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenu(null);
                          nav({ page: "dashboard" });
                        }}
                      >
                        <span className="ic">⚙</span>
                        {t("header.dashboard")}
                      </a>
                      <a
                        href="https://studio.pixadv.net"
                        target="_blank"
                        rel="noreferrer"
                        className="gdrop-item"
                      >
                        <span className="ic">↗</span>
                        {t("header.openInStudio")}
                        <span className="meta">studio.</span>
                      </a>
                    </div>
                    <div className="gdrop-section">
                      <a
                        href="#"
                        className="gdrop-item"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenu(null);
                        }}
                      >
                        <span className="ic">?</span>
                        {t("header.help")}
                      </a>
                      <a
                        href="#"
                        className="gdrop-item"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenu(null);
                          onLogout();
                        }}
                      >
                        <span className="ic">⏻</span>
                        {t("header.logout")}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {openMenu === "notif" && (
                <div className="gdrop" style={{ right: 60, top: 48, minWidth: 320 }}>
                  <div className="gdrop-header">
                    {t("header.notificationsTitle")}{" "}
                    <span style={{ color: "var(--accent)" }}>
                      ·{" "}
                      {t("header.notificationsNewCount", {
                        count: notifications.length,
                      })}
                    </span>
                  </div>
                  {notifications.length === 0 ? (
                    <div
                      style={{
                        padding: 14,
                        fontSize: 12,
                        color: "var(--muted-2)",
                        fontFamily: "var(--font-mono)",
                        textAlign: "center",
                      }}
                    >
                      ─
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <a
                        key={n.id}
                        href="#"
                        className="gdrop-item"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenu(null);
                          if (n.workId) nav({ page: "work", id: n.workId });
                        }}
                      >
                        <span
                          className="ic"
                          style={{
                            color:
                              n.type === "sale"
                                ? "var(--success)"
                                : n.type === "review"
                                  ? "var(--accent)"
                                  : n.type === "favorite"
                                    ? "var(--primary)"
                                    : "var(--muted-2)",
                          }}
                        >
                          {n.type === "sale"
                            ? "¥"
                            : n.type === "review"
                              ? "★"
                              : n.type === "favorite"
                                ? "♡"
                                : "i"}
                        </span>
                        <span style={{ fontSize: 12 }}>{n.text}</span>
                        <span className="meta">{n.at}</span>
                      </a>
                    ))
                  )}
                  <div
                    className="gdrop-section"
                    style={{ textAlign: "center", padding: "4px" }}
                  >
                    <a
                      href="#"
                      style={{
                        fontSize: 11,
                        fontFamily: "var(--font-mono)",
                        color: "var(--muted-2)",
                      }}
                    >
                      {t("header.viewAll")}
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => nav({ page: "auth", mode: "login" })}
              >
                {t("header.login")}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => nav({ page: "auth", mode: "signup" })}
              >
                {t("header.signup")}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function LangSwitch() {
  const { lang, setLang } = useI18n();
  return (
    <div className="lang-switch" role="group" aria-label="Language">
      <button
        className={lang === "ja" ? "active" : ""}
        onClick={() => setLang("ja")}
      >
        JA
      </button>
      <button
        className={lang === "en" ? "active" : ""}
        onClick={() => setLang("en")}
      >
        EN
      </button>
    </div>
  );
}

// =============================================================
// Mobile bottom tabs
// =============================================================

export function MobileTabs({ route, nav }: { route: Route; nav: Nav }) {
  const t = useT();
  const { user } = useAuth();
  const tabs: Array<{ id: string; icon: string; label: string; target: Route }> = [
    { id: "home", icon: "⌂", label: t("mobile.home"), target: { page: "home" } },
    { id: "search", icon: "⌕", label: t("mobile.search"), target: { page: "search" } },
    {
      id: "library",
      icon: "▣",
      label: t("mobile.library"),
      target: { page: "library" },
    },
    {
      id: "author",
      icon: "@",
      label: t("mobile.profile"),
      target: { page: "author", id: user?.id },
    },
  ];
  return (
    <div className="mobile-tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          className={`mt ${route.page === t.id ? "active" : ""}`}
          onClick={() => nav(t.target)}
        >
          <span className="mt-ic">{t.icon}</span>
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  );
}
